import { UpdateSurveyRequest } from "data/slices/surveylist/interface";
import i18n from "i18n";
import keys from "locale/keys";
import { isEqual } from "lodash";
import { SURVEY_STATUS } from "utils/constants";
import { DynataStatus } from "utils/DynataStatus";

import {
  ClientMessages,
  MarketplaceMessages,
  TOLUNA_MESSAGES,
} from "../../utils/survey-response-codes";

/*
  Precision Sample
  https://panel-staging.surveyspotter.com/survey-redirect?sub_id3=33545tgh567768&surveyid=1080000037&Status=S&survey_complete_date=11/28/2024&gross=2.50&reward=1.80
 
  S,means member completed the Surver preliminarily.
  T,means member failed ot terminated from the Survey.
  Q,means member quota full for the Survey.
  R,means member completed the Survey Preliminary but finally rejected by the Client. Initially when the member completes the survey preliminarily we fire a Pixel with status S and then after 2 to 3 weeks (means,When we have the Updated final complets list from the client side people),if this member is not in the final completed list we fire the pixel with Status R.So always the status "S" indicates that member can get reward and the Status "R" cancels the rewards issued preliminarily(Status=S).
  F,means client approved the Preliminary complete as Final Complete
 
 
  CINT 
  https://panel-staging.surveyspotter.com/survey-redirect?pid=xyz&sur=1234567&l=10&c=1.50&mid=abc&42=53&43=2&45=70130&639=10010000000&hash=RiNPoR2Bu4ZqhGXQrcK6IL2BcRElctks

  Variable	Returned on	                Description
  pid	      Complete, Termination	      Unique respondent identifier created by the supplier.
  mid	      Complete, Termination	      Optional secondary identifier passed in on entry. Value returns blank if not passed in on entry.
  sur	      Complete	                  Completed survey ID.
  c	        Complete	                  Gross payout per complete in USD. This value is before any applicable commissions or fees.
  l	        Complete	                  Median LOI of the completed survey. Note: until the survey has reached 6 completes, the parameter l will be returned as 0.
  [%questionID%]	Complete, Termination	Standard qualification questionID. Precode value returned if data is stored for the respondent. Value returns blank if no data stored. questionIDs may be returned.           
  hash	    Complete, Termination	      HMAC SHA1 hash signature of the entire URL. Used by the supplier to validate whether the URL has been manipulated during redirect.


  Dynata
  https://panel-staging.surveyspotter.com/survey-redirect?disposition=1&status=0&access_key=1234&expiration=2021-10-19T17:48:36.480Z&signature=c156e91013eb715edb2b07df5491497a807206b1796bb7d031505107a9dbdcdf


  name	          disposition	    description
  Unknown/Error	  0	              An unknown state was reached or an error was encountered.
  Complete	      1	              The respondent completed the client survey.
  Disqualified	  2	              The respondent was disqualified.
  Over Quota	    3	              The respondent qualified for a full quota and was terminated.
  Duplicate	      4	              The respondent already participated in the client survey.
                                  Once a respondent is sent into a survey, the same respondent should not be sent back to the same survey, dedupe on start using project_id. Typically, if a respondent hits a conclusion, even if the survey reopens, they will hit a duplicate if they enter again.
                                  For partners with both Registry and Router, Registry and Router traffic needs to be deduped against project_id, Dynata can add project_id to router redirects for partners to then dedupe when sending traffic through registry.
  Quality	        5	              The respondent was disqualified by Dynata due to failing quality checks.
 */

export const TolunaRoutes = [
  "/surveys/toluna/not-qualified",
  "/surveys/toluna/quota-full",
  "/surveys/toluna/fraud-terminate",
  "/surveys/toluna/terminated",
  "/surveys/toluna/max-surveys-reached",
  "/surveys/toluna/no-surveys",
  "/surveys/toluna/no-cookies",
  "/surveys/toluna/survey-not-available",
  "/surveys/toluna/survey-taken",
];

export const isSurveySucccess = async (
  pathname: string,
  params?: URLSearchParams,
): Promise<boolean> => {
  if (!params && !isToluna(pathname)) {
    return false;
  }

  // toluna
  if (isToluna(pathname)) {
    // Here we are check toluna status is qualified or not to show the success or failure message in redirection page.
    return isTolunaSuccess(pathname, params);
  }

  if (!params) return false;

  //precision sample
  if (isPrecisionSample(params)) {
    const status = params?.get("Status")?.toLowerCase();
    console.log(`precision sample -> status --> ${status}`);
    return isPrecisionSampleSuccess(status);
  }

  //lucid measurement
  if (isLucidMeasurement(params)) {
    const status = params?.get("ProofStatus");
    console.log(`lucid measurement -> ProofStatus --> ${status}`);
    return isLucidMeasurementSuccess(status);
  }

  //cint
  if (isCINT(params)) {
    console.log(`cint -> status --> ${params?.get("c")}`);
    return isCINTSuccess(params); //just check if url has gross payout param
  }

  //dynata
  if (isDynata(params)) {
    const status = params?.get("disposition")?.toLowerCase();
    console.log(`dynata -> disposition --> ${status}`);
    return isDynataSuccess(status);
  }

  return false;
};

export const getRequestPayload = (
  userId: string,
  pathname: string,
  params?: URLSearchParams,
  tolunaUrl?: string,
): UpdateSurveyRequest | undefined => {
  if (!params && !isToluna(pathname)) {
    return undefined;
  }

  // toluna
  if (isToluna(pathname)) {
    return createTolunaPayload(tolunaUrl, pathname);
  }

  //precision sample
  if (isPrecisionSample(params)) {
    const status = params.get("Status")?.toLowerCase();
    const surveyId = params.get("surveyid");
    const surveyStatus = isPrecisionSampleSuccess(status)
      ? SURVEY_STATUS.COMPLETED
      : SURVEY_STATUS.FAILED;
    return createPayload(userId, surveyId, surveyStatus);
  }

  //lucid measurement
  if (isLucidMeasurement(params)) {
    const status = params.get("ProofStatus");
    const surveyId = params.get("ProofSurveyNumber");
    const surveyStatus = isLucidMeasurementSuccess(status)
      ? SURVEY_STATUS.COMPLETED
      : SURVEY_STATUS.FAILED;
    return createLucidMeasurementPayload(
      userId,
      surveyId,
      surveyStatus,
      params,
    );
  }

  //cint
  //for cint, for now if redirected then treat as success
  if (isCINT(params)) {
    const surveyId = params.get("survey_id");
    return createCINTPayload(userId, surveyId, SURVEY_STATUS.COMPLETED, params);
  }

  //dynata
  //TODO: need to confirm how to get the survey id in url
  if (isDynata(params)) {
    const disposition = params?.get("disposition")?.toLowerCase();
    const status = params?.get("status")?.toLowerCase();
    const surveyId = params.get("surveyId");
    const surveyStatus = isDynataSuccess(disposition)
      ? SURVEY_STATUS.COMPLETED
      : SURVEY_STATUS.FAILED;
    return {
      status: surveyStatus,
      surveyId,
      userId,
      dispositionId: disposition,
      statusId: status,
    };
  }

  return undefined;
};

const isPrecisionSample = (params?: URLSearchParams) => {
  return params?.has("surveyid");
};

const isCINT = (params?: URLSearchParams) => {
  return params?.has("pid");
};

export const isDynata = (params?: URLSearchParams) => {
  return params?.has("signature");
};

export const isLucidMeasurement = (params?: URLSearchParams) => {
  return params?.get("Proof") === "1";
};

export const isToluna = (pathname: string) => {
  return pathname.includes("toluna");
};

export const isTolunaSuccess = (pathname: string, params?: URLSearchParams) => {
  return (
    params?.get("status") === "qualified" || pathname.includes("/qualified")
  );
};

const isPrecisionSampleSuccess = (status?: string) => {
  return isEqual(status, "s") || isEqual(status, "f");
};

const isCINTSuccess = (params?: URLSearchParams): boolean => {
  return !!params?.get("c");
};

const isDynataSuccess = (status?: string) => {
  return isEqual(status, "1");
};

const isLucidMeasurementSuccess = (status?: string | null) => {
  return status === "70";
};

const createPayload = (
  userId: string,
  surveyId: string,
  status: string,
): UpdateSurveyRequest => {
  return {
    status,
    surveyId,
    userId,
  };
};

const createTolunaPayload = (
  tolunaUrl: string,
  pathname: string,
): UpdateSurveyRequest => {
  const status = isToluna(pathname)
    ? SURVEY_STATUS.COMPLETED
    : SURVEY_STATUS.FAILED;
  return {
    status,
    tolunaUrl,
  };
};

const createCINTPayload = (
  userId: string,
  surveyId: string,
  status: string,
  params?: URLSearchParams,
): UpdateSurveyRequest => {
  // Pick shared extra params when present
  const marketplaceStatus = params?.get("marketplace_status") ?? null;
  const clientStatus = params?.get("client_status") ?? null;
  const rpi = params?.get("rpi") ?? null;
  const hash = params?.get("hash") ?? "";
  const mid = params?.get("mid") ?? "";
  const pid = params?.get("pid") ?? "";

  // Extract CINT questions (numeric keys)
  const cintQuestions: Record<string, string> = {};
  if (params) {
    params.forEach((value, key) => {
      // Check if the key is numeric (CINT question ID)
      if (/^\d+$/.test(key)) {
        cintQuestions[key] = value.startsWith("0")
          ? value.replace(/0/g, "")
          : value;
      }
    });
  }

  return {
    status,
    surveyId,
    userId,
    marketplaceStatus,
    clientStatus,
    rpi,
    hash,
    mid,
    pid,
    cintQuestions:
      Object.keys(cintQuestions).length > 0 ? cintQuestions : undefined,
  };
};

const createLucidMeasurementPayload = (
  userId: string,
  surveyId: string | null,
  status: string,
  params?: URLSearchParams,
): UpdateSurveyRequest => {
  const payload: UpdateSurveyRequest = {
    status,
    surveyId: surveyId ?? "",
    userId,
  };

  if (params?.has("ProofCPI")) {
    payload.ProofCPI = params.get("ProofCPI");
  }

  return payload;
};

export const getDynataStatusMessage = (
  disposition: string,
  status: string,
  marketplaceStatus?: string | number, // Accept string or number
  clientStatus?: string | number, // Accept string or number
): string => {
  // 1. Check Client Status First (Highest Priority)
  // We convert to Number() because your inputs might be strings "40", but the Map keys are numbers 40.
  if (clientStatus) {
    const msg = ClientMessages[Number(clientStatus)];
    if (msg) return msg;
  }

  // 2. Check Marketplace Status Second
  if (marketplaceStatus) {
    const msg = MarketplaceMessages[Number(marketplaceStatus)];
    if (msg) return msg;
  }

  // 3. Fallback to your existing Disposition-Status logic
  const key = `${disposition}-${status}`;
  const message = DynataStatus[key];

  // 4. Final Fallback to default generic message
  return message ? message : i18n.t(keys.survey_info_msg);
};

export const getTolunaStatusMessage = (
  pathname: string,
  params?: URLSearchParams,
): string => {
  return (
    TOLUNA_MESSAGES[pathname as keyof typeof TOLUNA_MESSAGES] ||
    (isTolunaSuccess(pathname, params)
      ? i18n.t(keys.survey_success_msg)
      : i18n.t(keys.survey_info_msg))
  );
};

export const getFailurePayload = (
  userId: string,
  params?: URLSearchParams,
  pathname?: string,
  tolunaUrl?: string,
):
  | {
      surveyId?: string;
      userId: string;
      clientStatus?: string;
      marketplaceStatus?: string;
      surveyNumber?: string;
      providerId?: number;
      status?: string;
      tolunaUrl?: string;
      additionalDetails?: null;
    }
  | undefined => {
  // Toluna doesn't provide detailed failure reason in URL params, so we will get the status from the URL/Path.
  if (pathname && TolunaRoutes.some((route) => pathname.includes(route))) {
    const pathParts = pathname.split("/");
    const status = pathParts[pathParts.length - 1];
    return {
      userId,
      status,
      tolunaUrl,
    };
  }

  if (!params) {
    return undefined;
  }

  const surveyId =
    params.get("surveyid") ||
    params.get("survey_id") ||
    params.get("surveyId") ||
    params.get("ProofSurveyNumber") ||
    params.get("SurveyID") ||
    "";
  const surveyNumber = surveyId;
  const clientStatus =
    params.get("client_status") || params.get("ProofStatus") || "-1";
  const marketplaceStatus =
    params.get("marketplace_status") || params.get("ProofStatus") || "37";
  const providerId = isLucidMeasurement(params) ? 1 : 4; // Lucid usually 1, others 4

  if (!surveyId) {
    return undefined;
  }

  return {
    surveyId,
    userId,
    clientStatus,
    marketplaceStatus,
    surveyNumber,
    providerId,
    additionalDetails: null,
  };
};
